import { isTelegramMode } from './app-mode';

/**
 * Реклама Adsgram в мини-приложении.
 *
 * Сеть работает только внутри Telegram: в браузере её скрипт бесполезен,
 * поэтому и не загружается. Настройки приходят с сервера — идентификатор
 * блока, моменты показа и минимальный промежуток между ними.
 *
 * Показ никогда не мешает работе приложения: любая неудача — отсутствие блока,
 * недоступность сети, отказ пользователя — молча пропускается. Реклама не
 * повод не дать человеку начать разговор.
 */

const SDK_URL = 'https://sad.adsgram.ai/js/sad.min.js';
const LAST_SHOWN_KEY = 'anonchat.adsLastShownAt';
/** Сколько приложение готово ждать показ, прежде чем продолжить без него. */
const SHOW_TIMEOUT_MS = 20_000;

export interface AdsSettings {
  adsgramEnabled: boolean;
  adsgramBlockId: string | null;
  adsgramOnChatEnd: boolean;
  adsgramOnSearchStart: boolean;
  adsgramMinIntervalSec: number;
}

/** Момент показа. Названия совпадают с настройками в панели. */
export type AdTrigger = 'chatEnd' | 'searchStart';

interface AdController {
  show(): Promise<{ done: boolean; description: string; state: string; error: boolean }>;
  destroy?(): void;
}

declare global {
  interface Window {
    Adsgram?: { init(params: { blockId: string; debug?: boolean }): AdController };
  }
}

let settings: AdsSettings | null = null;
let controller: AdController | null = null;
let sdkPromise: Promise<boolean> | null = null;

export function configureAds(next: AdsSettings): void {
  settings = next;

  // Скрипт подгружается заранее, до первого показа: иначе первая реклама
  // ждала бы загрузку сети уже на глазах у человека.
  if (enabled()) {
    void loadSdk();
  }
}

function enabled(): boolean {
  return Boolean(isTelegramMode && settings?.adsgramEnabled && settings.adsgramBlockId);
}

function loadSdk(): Promise<boolean> {
  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = new Promise<boolean>((resolve) => {
    if (window.Adsgram) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => resolve(Boolean(window.Adsgram));
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return sdkPromise;
}

/** Прошло ли достаточно времени с прошлого показа. */
function allowedByInterval(): boolean {
  const interval = (settings?.adsgramMinIntervalSec ?? 0) * 1000;
  if (interval <= 0) {
    return true;
  }

  try {
    const last = Number(localStorage.getItem(LAST_SHOWN_KEY) ?? 0);
    return Date.now() - last >= interval;
  } catch {
    // Приватный режим: ограничение по времени не работает, но показ разрешаем.
    return true;
  }
}

function rememberShown(): void {
  try {
    localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
  } catch {
    // Ограничение частоты продержится до перезагрузки — это допустимо.
  }
}

function triggerAllowed(trigger: AdTrigger): boolean {
  if (!settings) {
    return false;
  }
  return trigger === 'chatEnd' ? settings.adsgramOnChatEnd : settings.adsgramOnSearchStart;
}

/**
 * Показывает рекламу, если она включена, момент подходит и промежуток выдержан.
 * Возвращает управление после закрытия объявления — вызывающий код может
 * дождаться, но не обязан.
 */
export async function maybeShowAd(trigger: AdTrigger): Promise<void> {
  if (!enabled() || !triggerAllowed(trigger) || !allowedByInterval()) {
    return;
  }

  try {
    const ready = await loadSdk();
    if (!ready || !window.Adsgram) {
      return;
    }

    controller ??= window.Adsgram.init({ blockId: settings!.adsgramBlockId! });

    // Отметка ставится до показа: если человек закроет мини-приложение прямо
    // на объявлении, повтор при следующем открытии выглядел бы навязчиво.
    rememberShown();

    // Ожидание ограничено: показ перед поиском задерживает начало разговора,
    // и зависший объявляющий скрипт не должен запирать человека на экране
    // анкеты. Реклама при этом не прерывается — просто перестаёт удерживать
    // приложение.
    await Promise.race([controller.show(), timeout(SHOW_TIMEOUT_MS)]);
  } catch {
    // Блок не найден, сеть недоступна, показ прерван — приложение продолжает
    // работать как ни в чём не бывало.
  }
}

const timeout = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));
